import z from 'zod';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { CommanderFormProps, useDialogForm, useHandledDialogFormControls } from '@mews-commander/form';
import { createZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import { updateDetailSchema } from '../../ConfigureProductForm/formEnums';
import { DetailsForm } from '../../ConfigureProductForm/steps/DetailsForm';
import { CentralProductDetailsProps } from '../../CentralProductSidePanel/CentralProductDetails';
import { handleImagePayload } from '../../../utils/handleImagePayload';

type DetailsProductFormValues = z.infer<typeof updateDetailSchema>;

interface EditEnterpriseProductDetailProps {
    propertyId: string;
    enterpriseProductId: string;
    productData: CentralProductDetailsProps;
}

export const useEditEnterpriseProductDialogForm = ({
    enterpriseProductId,
    propertyId,
    productData,
}: EditEnterpriseProductDetailProps) => {
    return useDialogForm({
        id: 'enterpriseProductEditDetailsForm',
        title: lt('EditProductDetails'),
        cancelLabel: lt('GoBack'),
        renderForm: (formProps) => (
            <EditProductDetailsForm
                {...formProps}
                enterpriseProductId={enterpriseProductId}
                propertyId={propertyId}
                productData={productData}
            />
        ),
        size: 'medium',
    });
};

const EditProductDetailsForm = ({
    propertyId,
    enterpriseProductId,
    productData,
    ...formProps
}: EditEnterpriseProductDetailProps & CommanderFormProps) => {
    const { mutateAsync } = useRepositoryMutation('portfolioEnterpriseProducts', 'updateDetails', {
        onSuccess: () => {
            showSuccessAlert({ title: lt('EnterpriseProductEditDetailsSuccessMessage') });
        },
        invalidateQueries: {
            portfolioEnterpriseProducts: ['getDetail'],
        },
    });

    const { renderFormContent } = useHandledDialogFormControls<DetailsProductFormValues>({
        ...formProps,
        onSubmit: async (values) => {
            const { externalNames, descriptions } = Object.entries(values.languages).reduce(
                (acc, [locale, languageValues]) => {
                    if (languageValues) {
                        acc.externalNames[locale] = languageValues.externalName ?? '';
                        acc.descriptions[locale] = languageValues.description ?? '';
                    }
                    return acc;
                },
                {
                    externalNames: {} as Record<string, string>,
                    descriptions: {} as Record<string, string>,
                }
            );

            const imageValue = await handleImagePayload(values.image);

            await mutateAsync({
                centralProductId: productData.centralProductId ?? '',
                propertyId,
                enterpriseProductId,
                externalNames,
                descriptions,
                imageId: values.imageId,
                image: imageValue,
                externalIdentifier: values.externalIdentifier,
            });
        },
        resolver: createZodFormResolver(updateDetailSchema),
        defaultValues: {
            languages: productData.localizedData,
            externalIdentifier: productData.externalIdentifier ?? '',
        },
    });

    return renderFormContent(<DetailsForm isEditForm imageId={productData.imageId} />);
};
